import React, { useState } from 'react';
import { SellerLayout } from '../SellerLayout';
import {
  PageHeading,
  CardBalance,
  Pills,
  Button,
  Text,
  Avatar,
  Icon,
} from 'barte-design-system';
import { LockIcon } from '@phosphor-icons/react';
import './PrimeiraEntrega.css';

// ─── Ícone do card travado (mesmo padrão do DashboardSaldosV4) ───────────────

const BolsaoLockIcon: React.ElementType = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
    <Icon icon={LockIcon} size={16} />
  </span>
);

// ─── Types ───────────────────────────────────────────────────────────────────

/** Estados do bolsão previstos no UX_BRIEF. */
type BolsaoStatus = 'zerado' | 'retendo' | 'teto';

type PillState = 'default' | 'success' | 'alert' | 'info';

type PeriodKey = 'week' | 'month' | 'jan';

// ─── Mock data ───────────────────────────────────────────────────────────────

const SELLER_NOME = 'Café Central';

const DISPONIVEL = 40000;
const FUTUROS = 120000;
const BOLSAO_RETIDO = 2450;

/** Estado exibido neste protótipo. */
const BOLSAO_STATUS_ATUAL: BolsaoStatus = 'retendo';

const CHART_DATA = [
  { label: '01', received: 0, overdue: 0 },
  { label: '03', received: 0, overdue: 0 },
  { label: '05', received: 0, overdue: 0 },
  { label: '07', received: 0, overdue: 0 },
  { label: '08', received: 45, overdue: 40, isToday: true },
  { label: '11', received: 0, overdue: 0 },
  { label: '13', received: 0, overdue: 0 },
  { label: '15', received: 0, overdue: 0 },
  { label: '17', received: 0, overdue: 0 },
  { label: '18', received: 0, overdue: 0 },
  { label: '21', received: 0, overdue: 0 },
  { label: '23', received: 0, overdue: 0 },
  { label: '25', received: 0, overdue: 0 },
  { label: '27', received: 0, overdue: 0 },
  { label: '28', received: 0, overdue: 0 },
  { label: '31', received: 0, overdue: 0 },
];

const MAX_CHART_VALUE = Math.max(...CHART_DATA.map((d) => d.received + d.overdue), 1);

const RECEBIVEIS_MOCK = [
  {
    id: '1',
    initials: 'AL',
    name: 'Ana Lima',
    dueLabel: 'venc. 06/01',
    amount: 'R$ 248,90',
    status: 'Em atraso' as const,
    statusState: 'error' as const,
  },
  {
    id: '2',
    initials: 'CM',
    name: 'Carlos Mendes',
    dueLabel: 'venc. 06/01',
    amount: 'R$ 1.320,00',
    status: 'Paga - 06/01' as const,
    statusState: 'success' as const,
  },
];

const BOLSAO_STATUS: Record<BolsaoStatus, { label: string; state: PillState }> = {
  zerado: { label: 'Sem retenção ainda', state: 'default' },
  retendo: { label: 'Retendo', state: 'info' },
  teto: { label: 'Teto atingido', state: 'success' },
};

/**
 * O chip do bolsão no appbarte usa o mesmo mapa do payfac.
 * O dilema anterior caiu junto com "abaixo do esperado": os estados que sobraram
 * (sem retenção ainda, retendo, teto atingido) são fato, não julgamento de risco.
 * 'neutro' fica como alternativa, caso o time prefira um rótulo de categoria.
 */
const BOLSAO_CHIP_MODE: 'status' | 'neutro' = 'status';

const CHIP_NEUTRO = { label: 'Garantia operacional', state: 'default' as PillState };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ─── Component ───────────────────────────────────────────────────────────────

export function PrimeiraEntrega() {
  const [activePeriod, setActivePeriod] = useState<PeriodKey>('month');
  const temBolsao = true;
  const chipBolsao =
    BOLSAO_CHIP_MODE === 'status' ? BOLSAO_STATUS[BOLSAO_STATUS_ATUAL] : CHIP_NEUTRO;

  return (
    <SellerLayout activeItem="primeira-entrega">
      <main className="content">
        <div className="content-inner">
          <PageHeading title={`Olá, Bem-vindo ${SELLER_NOME}!`} />

          <div className="primeira-entrega">
            <div className="pe-cards-row" data-cards={temBolsao ? 3 : 2}>
              <div className="pe-card-wrapper">
                <CardBalance className="pe-card pe-card--disponivel">
                  <CardBalance.Info
                    variant="big"
                    title="Disponível para saque"
                    balance={formatBRL(DISPONIVEL)}
                    showIcon={false}
                    showPills
                    pills={
                      <Pills label="Saldo liberado" state="success" variant="light" size="sm" />
                    }
                    rightSlot={
                      <div className="card-action">
                        <Button variant="fill" size="sm" round="full" onClick={() => undefined}>
                          Solicitar saque
                        </Button>
                      </div>
                    }
                  />
                </CardBalance>
              </div>

              <div className="pe-card-wrapper">
                <CardBalance className="pe-card pe-card--futuros">
                  <CardBalance.Info
                    variant="big"
                    title="Lançamentos futuros"
                    balance={formatBRL(FUTUROS)}
                    showIcon={false}
                    showPills
                    pills={
                      <Pills label="Saldo a liberar" state="accent" variant="light" size="sm" />
                    }
                    rightSlot={
                      <div className="card-action">
                        <Button variant="outline" size="sm" round="full" onClick={() => undefined}>
                          Antecipar
                        </Button>
                      </div>
                    }
                  />
                </CardBalance>
              </div>

              {temBolsao && (
                <div className="pe-card-wrapper">
                  {/* Sem clique e sem "Ver detalhes": no appbarte não existe
                      detalhe nem configuração de bolsão.
                      [A DEFINIR] — ponto 4 da UX_SPEC: o texto da explicação do
                      bolsão entra aqui como bottomSlot quando estiver definido. */}
                  <CardBalance className="pe-card pe-card--bolsao">
                    <CardBalance.Info
                      variant="big"
                      title="Bolsão"
                      balance={formatBRL(BOLSAO_RETIDO)}
                      icon={BolsaoLockIcon}
                      showPills
                      pills={
                        <Pills
                          label={chipBolsao.label}
                          state={chipBolsao.state}
                          variant="light"
                          size="sm"
                        />
                      }
                    />
                  </CardBalance>
                </div>
              )}
            </div>

            {/* ── Seção Recebíveis (mesma das outras páginas de Dash Saldos) ── */}
            <div className="receivables-section">
              <div className="receivables-columns">
                {/* Coluna esquerda: gráfico */}
                <div className="receivables-chart-col">
                  <div className="receivables-header">
                    <Text variant="label-lg">Recebíveis</Text>
                    <div className="period-buttons">
                      <Button
                        variant={activePeriod === 'week' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('week')}
                      >
                        Semana
                      </Button>
                      <Button
                        variant={activePeriod === 'month' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('month')}
                      >
                        Mês
                      </Button>
                      <Button
                        variant={activePeriod === 'jan' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('jan')}
                      >
                        janeiro/2026
                      </Button>
                    </div>
                  </div>

                  <div className="chart-container">
                    <div className="chart-bars">
                      {CHART_DATA.map((d) => {
                        const receivedPct = (d.received / MAX_CHART_VALUE) * 100;
                        const overduePct = (d.overdue / MAX_CHART_VALUE) * 100;
                        return (
                          <div key={d.label} className="chart-col">
                            <div className="bar-group">
                              <div
                                className="bar bar-received"
                                style={{ height: `${receivedPct}%` }}
                              />
                              <div
                                className="bar bar-overdue"
                                style={{ height: `${overduePct}%` }}
                              />
                            </div>
                            <Text
                              variant="caption-sm"
                              style={{
                                color: d.isToday
                                  ? 'var(--content-primary)'
                                  : 'var(--content-tertiary)',
                                textAlign: 'center',
                                fontWeight: d.isToday ? 600 : undefined,
                              }}
                            >
                              {d.isToday ? 'hoje' : d.label}
                            </Text>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="chart-legend">
                    <div className="legend-item">
                      <span className="legend-dot legend-received" />
                      <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
                        Recebidos
                      </Text>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot legend-overdue" />
                      <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
                        Em atraso
                      </Text>
                    </div>
                  </div>
                </div>

                {/* Coluna direita: lista de recebíveis */}
                <div className="receivables-list-col">
                  <div className="receivables-list-header">
                    <Text variant="label-lg">Recebíveis</Text>
                    <Button variant="link" size="sm">
                      ver todos
                    </Button>
                  </div>
                  <div className="receivables-search-placeholder" />
                  <div className="receivables-list">
                    {RECEBIVEIS_MOCK.map((item) => (
                      <div key={item.id} className="receivable-item">
                        <Avatar variant="text" size="md" initials={item.initials} />
                        <div className="receivable-item-info">
                          <Text variant="label-sm">{item.name}</Text>
                          <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
                            {item.dueLabel}
                          </Text>
                        </div>
                        <div className="receivable-item-right">
                          <Text variant="label-sm">{item.amount}</Text>
                          <Text
                            variant="caption-sm"
                            style={{
                              color:
                                item.statusState === 'error'
                                  ? 'var(--accent-red)'
                                  : 'var(--accent-green)',
                            }}
                          >
                            {item.status}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </SellerLayout>
  );
}
